import { BaseComponent } from '../base-component/base-component';
import { customElement, property } from 'lit/decorators.js';
import { html } from 'lit';
import { connect } from 'pwa-helpers';
import store from '../../store/store';
import { StoreState } from '../../store/store.interface';
import { smallSpinner } from '../../common/constants/api-constants';
import { state } from 'lit/decorators.js';
import { nothing } from 'lit-html';
import { ordinalSuffixOf } from '../../common/utils/util';
import { ariaLabelWithNewTab } from '../../common/utils/a11y-util';
import { getImageSrcUrl } from '../../common/utils/product-util';
import { getCartItemFormat } from '../../common/utils/cart-util';
import { FreeTrialProduct } from './interfaces/free-trial-product.interface';
import { cancelFreeTrial } from './thunks/free-trial-cancel.thunk';
import { FreeTrialCancelRequest } from './interfaces/free-trial-cancel.interface';
import { getFreeTrialCancelData } from './store/free-trial.selector';
import { getFreeTrialDuration, isActiveFreeTrial } from './utils/free-trial.util';
import environment from '../../common/environment';
import emit from '../../common/events';
import { getDateFromTimestamp } from '../../common/utils/date-utils';

/**
 * Button and modal for free trial canceling
 */
@customElement('mhecomm-free-trial-cancel')
export class FreeTrialCancel extends connect(store)(BaseComponent) {

  /**
   * Additional CSS classes for the cancel button.
   */
  @property()
  addClass = '';

  /**
   * Button text
   */
  @property()
  cancelFreeTrialButtonText = 'Cancel Free Trial';

  /**
   * Modal header text
   */
  @property()
  modalHeader = 'Are you sure you want to cancel your free trial for this item?';

  /**
   * A JSON String in the format of 'FreeTrialProduct'.
   */
  @property({type: Object})
  product?: FreeTrialProduct;

  /**
   * Order number
   */
  @property()
  orderNumber = '';

  @state()
  private isCanceling = false;

  private modal;
  private isModalVisible = false;
  private cancelFailed = false;

  updated() {
    this.modal = this.bsInit('#free-trial-cancel-model', window.bootstrap?.Modal)?.instance;
  }

  renderComp() {
    return this.product?.freeTrialInfo && isActiveFreeTrial(this.product.freeTrialInfo) ? html`
      <button
        class="btn btn-outline-primary w-100 w-lg-75 ${this.addClass}"
        data-automation-id="btn-cancelfreetrial-${this.orderNumber}-${this.product?.isbn10}"
        @click=${this._openModal}
        ?disabled=${this.isCanceling}>
        ${this.cancelFreeTrialButtonText} ${this.isCanceling ? smallSpinner() : nothing}
      </button>
      <div
        class="modal fade"
        id="free-trial-cancel-model"
        tabindex="-1"
        aria-hidden="${this.isModalVisible ? 'false' : 'true'}"
        aria-modal="${this.isModalVisible ? 'true' : 'false'}"
        aria-label=${this.modalHeader}
        role="${this.isModalVisible ? 'dialog' : nothing}"
        aria-labelledby="free-trail-cancel-modal">
        <div class="modal-dialog modal-fullscreen-md-down text-start" role="document">
          <div class="modal-content">
            <div class="modal-header align-items-start">
              <h5 class="modal-title" id="free-trail-cancel-modal">
                ${this.modalHeader}
              </h5>
              <button
                type="button"
                class="btn-close"
                @click=${() => this._closeModal()}
                data-bs-dismiss="modal"
                aria-label="close">
              </button>
            </div>
            <div class="modal-body">
              ${this._renderFreeTrialProductInfo()}
              <div class="mt-3">
                If you cancel now, you will lose access to
                ${this.product ? this.product.productDescription : 'this product'} at the end of your
                ${getFreeTrialDuration(this.product?.freeTrialInfo?.startDate, this.product?.freeTrialInfo?.endDate)}-day free trial, and you will not be billed.
              </div>
              ${this.cancelFailed ? this._renderErrorInfo() : nothing}
            </div>
            <div class="modal-footer">
              <button
                id="modal-primary-button"
                type="button"
                class="btn btn-outline-primary w-100 w-md-auto"
                aria-label="cancel"
                @click=${() => this._closeModal()}
                data-bs-dismiss="modal">
                Continue Plan
              </button>
              <button
                id="modal-secondary-button"
                type="button"
                class="btn btn-outline-primary w-100 w-md-auto"
                aria-label="confirm"
                ?disabled=${this.isCanceling}
                @click=${() => this._cancelFreeTrial()}>
                ${this.cancelFreeTrialButtonText} ${this.isCanceling ? smallSpinner() : nothing}
              </button>
            </div>
          </div>
        </div>
      </div>
    ` : html``;
  }

  private _renderFreeTrialProductInfo() {
    return this.product ? html`
      <div class="bg-light p-3 rounded-3">
        <div class="row p-2">
          <div class="col-4 mb-3">
            <img alt=${this.product.productDescription} class="img-fluid"
                 src=${getImageSrcUrl(this.product.coverImage ? this.product.coverImage : this.product.productIsbn)}/>
          </div>
          <div class="col-md-8">
            <h3 class="h4 fs-large fw-bold">
              ${this.product.productDescription}${this.product.edition && parseInt(this.product.edition) > 0 ? `, ${ordinalSuffixOf(this.product.edition)} Edition` : ''}
              <br/>
            </h3>
            <mhecomm-free-trial-end-date .freeTrial="${this.product.freeTrialInfo}"></mhecomm-free-trial-end-date>
            <span class="fw-bold">ISBN10:</span>
            ${this.product.isbn10}
            ${this.product.isbn13 ? html`
              |
              <span class="fw-bold">ISBN13:</span>
              ${this.product.isbn13}` : nothing}
            <div>©${this.product.copyrightYear}</div>
            ${this.product.formatDescription ? html`
              <div>
                <span class="fw-bold">Format:</span>
                ${getCartItemFormat(this.product.formatDescription, this.product.formatCode, this.product.virtual)}
              </div>` : nothing}
          </div>
        </div>
      </div>` : nothing;
  }

  private _renderErrorInfo() {
    return html`
      <div class="col-12">
        <div class="alert alert-danger py-2 fs-6 invalid-feedback d-block mt-3 mb-0" role="alert" aria-live="assertive">
          <div class="row">
            <div class="col-1">
              <uxt-icon icon="exclamation-triangle" size="lg"></uxt-icon>
            </div>
            <div class="col-11">
              That didn’t seem to work... We’re sorry for the
              inconvenience. Please try again, and if the problem persists reach out to
              <a href="${environment().customerServiceUrl}" target="_blank" aria-label="${ariaLabelWithNewTab('customer support')}">customer support</a>.
            </div>
          </div>
        </div>
      </div>`
  }

  private _openModal() {
    this._openModalCancellationAnalytics();
    this.modal.show();
    this.isModalVisible = true;
  }

  private _closeModal(isSucessfulCancellation = false) {
    this.modal.hide();
    this.isModalVisible = false;
    if (isSucessfulCancellation) {
      return;
    }
    this._closeModalCancellationAnalytics();
  }

  private _fireEvent() {
    const event = new CustomEvent('mhecomm-free-trial-canceled', {bubbles: true});
    try {
      this.dispatchEvent(event);
    } catch (exception) {
      this._closeModal();
    }
  }

  private _cancelFreeTrial() {
    this.isCanceling = true;
    this.cancelFailed = false;
    this._requestCancelAnalytics();
    store.dispatch(cancelFreeTrial(this._getCancelRequest()))
      .then(() => this._processResponse())
      .catch((error) => {
        console.log('Error:', error);
        this._failureCancellationAnalytics()
        this.isCanceling = false;
        this.cancelFailed = true;
      });
  }

  private _processResponse() {
    this.isCanceling = false;
    const cancelResponse = getFreeTrialCancelData(store.getState() as StoreState);
    if (cancelResponse.length == 0) {
      this.cancelFailed = true;
    } else {
      const cancelResult = cancelResponse[0];
      this.cancelFailed = !cancelResult.success;
      if (cancelResult.success) {
        this._successCancellationAnalytics()
        this._fireEvent();
        this._closeModal(true);
      }
    }
  }

  private _getCancelRequest(): FreeTrialCancelRequest {
    return {
      orderNumber: this.orderNumber,
      skus: [`OP${this.product?.isbn13}`]
    };
  }

  private _successCancellationAnalytics() {
    emit({
      element: this,
      name: 'click',
      detail: {
        eventName: "freetrial-cancellation-successful",
        orderNumber: this.orderNumber,
        purchaseOrderNumber: this.product?.freeTrialInfo?.purchaseOrderNumber,
        isbn: this.product?.isbn13,
        placedOrderDate: getDateFromTimestamp(this.product?.freeTrialInfo?.startDate!),
        cancellationDate: new Date().toLocaleDateString()
      }
    })
  }

  private _failureCancellationAnalytics() {
    emit({
      element: this,
      name: 'click',
      detail: {
        eventName: "freetrial-cancellation-failed",
        orderNumber: this.orderNumber,
        purchaseOrderNumber: this.product?.freeTrialInfo?.purchaseOrderNumber,
        isbn: this.product?.isbn13,
      }
    })
  }

  private _requestCancelAnalytics() {
    emit({
      element: this,
      name: 'click',
      detail: {
        eventName: 'request-freetrial-cancellation',
        orderNumber: this.orderNumber,
        purchaseOrderNumber: this.product?.freeTrialInfo?.purchaseOrderNumber,
        isbn: this.product?.isbn13
      }
    })
  }

  private _openModalCancellationAnalytics() {
    emit({
      element: this,
      name: 'click',
      detail: {
        eventName: 'open-modal-cancellation-free-trial',
        orderNumber: this.orderNumber,
        purchaseOrderNumber: this.product?.freeTrialInfo?.purchaseOrderNumber,
        isbn: this.product?.isbn13
      }
    })
  }

  private _closeModalCancellationAnalytics() {
    emit({
      element: this,
      name: 'click',
      detail: {
        eventName: 'close-modal-cancellation-free-trial',
        orderNumber: this.orderNumber,
        purchaseOrderNumber: this.product?.freeTrialInfo?.purchaseOrderNumber,
        isbn: this.product?.isbn13
      }
    })
  } 
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-free-trial-cancel': FreeTrialCancel;
  }
}
